// TopBar notifications. The prototype's bell was decorative (hardcoded dot,
// unclickable). Now it derives real alerts from the dataset:
//   - overdue open action items
//   - key dates due within the next 14 days (or already blown)
// The badge only shows when there's something to see; the menu items
// navigate to the owning record.

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, ListChecks, CalendarClock } from 'lucide-react'
import { useStore } from '@/store/useStore'
import { useCurrentUser } from '@/auth/useCurrentUser'
import { isOwnedBy } from '@/lib/actionOwnership'
import { daysUntil, date as fmtDate } from '@/lib/format'
import type { Dataset, EntityRef } from '@/types/entities'

const MAX_ITEMS = 8

function routeForRef(d: Dataset, ref: EntityRef | undefined): string {
  if (!ref) return '/tasks'
  switch (ref.kind) {
    case 'account': return `/accounts/${ref.id}`
    case 'property': return `/properties/${ref.id}`
    case 'contact': return `/contacts/${ref.id}`
    case 'opportunity': return `/opportunities/${ref.id}`
    case 'fund': return `/funds/${ref.id}`
    case 'track': {
      const t = d.tracks.find((x) => x.id === ref.id)
      return t ? `/accounts/${t.accountId}` : '/tasks'
    }
    default: return '/tasks'
  }
}

function labelForRef(d: Dataset, ref: EntityRef | undefined): string | undefined {
  if (!ref) return undefined
  if (ref.label) return ref.label
  if (ref.kind === 'account') return d.accounts.find((a) => a.id === ref.id)?.name
  if (ref.kind === 'property') return d.properties.find((p) => p.id === ref.id)?.name
  return undefined
}

export function NotificationsBell() {
  const data = useStore((s) => s.data)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [open])

  const user = useCurrentUser()
  // PERSONAL alerts only (Chauncey 2026-07-13): MY overdue + due-soon tasks,
  // and key dates owned by me. Nobody wants the whole portfolio in the bell.
  const { overdueTasks, dueKeyDates, count } = useMemo(() => {
    const mine = data.actionItems.filter(
      (a) => a.status !== 'Closed' && isOwnedBy(a, user.email, user.displayName),
    )
    const overdueTasks = mine
      .map((a) => ({ item: a, du: daysUntil(a.dueDate) }))
      .filter((x) => x.du != null && x.du <= 7) // overdue + due within a week
      .sort((a, b) => (a.du! - b.du!))
    const dueKeyDates = data.keyDates
      .filter((k) => k.status !== 'Met' && k.status !== 'Waived' && k.owner === user.displayName)
      .map((k) => ({ item: k, du: daysUntil(k.date) }))
      .filter((x) => x.du != null && x.du <= 14)
      .sort((a, b) => (a.du! - b.du!))
    return { overdueTasks, dueKeyDates, count: overdueTasks.length + dueKeyDates.length }
  }, [data, user.email, user.displayName])

  const go = (to: string) => {
    setOpen(false)
    navigate(to)
  }

  const dueText = (du: number) =>
    du < 0 ? `${Math.abs(du)}d overdue` : du === 0 ? 'today' : `in ${du}d`

  return (
    <div className="notif-wrap" ref={ref}>
      <button
        className="topbar-icon"
        aria-label={count ? `Notifications (${count})` : 'Notifications'}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <Bell size={18} />
        {count > 0 && <span className="notif-count num">{count > 99 ? '99+' : count}</span>}
      </button>
      {open && (
        <div className="notif-menu" role="menu">
          {count === 0 && <div className="notif-empty dim">Nothing needs your attention.</div>}

          {overdueTasks.length > 0 && (
            <>
              <div className="notif-head label"><ListChecks size={13} /> Your tasks — overdue & due soon ({overdueTasks.length})</div>
              {overdueTasks.slice(0, MAX_ITEMS).map(({ item, du }) => (
                <button key={item.id} className="notif-item" role="menuitem" onClick={() => go(routeForRef(data, item.attachedTo))}>
                  <span className="notif-title">{item.title}</span>
                  <span className="notif-sub dim">
                    {labelForRef(data, item.attachedTo) ?? '—'} · {dueText(du!)}
                  </span>
                </button>
              ))}
            </>
          )}

          {dueKeyDates.length > 0 && (
            <>
              <div className="notif-head label"><CalendarClock size={13} /> Key dates (next 14 days)</div>
              {dueKeyDates.slice(0, MAX_ITEMS).map(({ item, du }) => (
                <button key={item.id} className="notif-item" role="menuitem" onClick={() => go(routeForRef(data, item.attachedTo))}>
                  <span className="notif-title">{item.title}</span>
                  <span className="notif-sub dim">
                    {labelForRef(data, item.attachedTo) ?? item.type} · {fmtDate(item.date)} ({dueText(du!)})
                  </span>
                </button>
              ))}
            </>
          )}

          <button className="notif-foot" onClick={() => go('/tasks')}>View all tasks</button>
        </div>
      )}
    </div>
  )
}
